import 'react-select/dist/react-select.css';

import React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import Select from 'react-select';
import { Errors } from 'react-redux-form';
import { formatText } from 'core/i18n';
import { isChrome } from 'utilities/browser';
import focusActions from 'actions/focusActions';
import { actions as formActions } from 'react-redux-form';
import { useFormValidation } from './hooks/useFormValidation';
import { useErrors } from './hooks/useErrors';
import { useHandlers } from './hooks/useFocus';
import { CLOSE_TIME_OUT_MS } from './constants';

const isNotValid = () => false;

const SelectListComponent = ({
    autoCompleteField = true,
    autofocus = false,
    classNameLabel = '',
    clearable,
    defaultValue = null,
    description = null,
    disabled = false,
    focusActions,
    focusAnimated = false,
    formActions,
    id,
    isLoading,
    isUpdated,
    label = '',
    messagesError = { required: formatText('validation-selectRequired') },
    onBlur,
    onChange,
    onClose,
    onKeyDown = null,
    onOpen,
    optionsList,
    placeholder,
    profileModel,
    profileModelLabel,
    searchable = false,
    style = {},
    tooltip,
    userProfile,
    userProfileForm,
    validations,
    value,
}) => {
    const modelProp = profileModel.split('.')[1];
    const isModelPropUndefined = typeof profileModel === 'undefined';
    const modelPropOrInvalidProp = !isModelPropUndefined ? modelProp : 'invalidProp';
    const userProfileFormModelProp = userProfileForm?.[modelProp];
    const investmentType = userProfile.investmentType;

    const { classError, changeClassError, handleInvalidFormError, renderErrors } = useErrors({
        profileModel,
        userProfileFormModelProp,
    });

    const { setTrigger, validateForm } = useFormValidation({
        formActions,
        handleInvalidFormError: () => handleInvalidFormError({ modelProp, investmentType }),
        isUpdated,
        profileModel,
        userProfileFormModelProp,
        validations,
    });

    const {
        autoBlur,
        classFocus,
        currentElement,
        handleOnBlur,
        handleOnChange: handleChange,
        handleOnFocus,
    } = useHandlers({
        autofocus,
        focusActions,
        focusAnimated,
        isModelPropUndefined,
        modelProp,
        modelPropOrInvalidProp,
        onBlur,
        searchable,
        setTrigger,
        userProfile,
        validateForm,
    });

    const formID = `id_wrapper_${modelPropOrInvalidProp}`;
    const formClassName = `form__select ${classFocus} ${classError}`;
    const inputClassName = `input-select_${modelPropOrInvalidProp}`;
    const inputProps = !autoCompleteField ? { autoComplete: isChrome() ? 'none' : 'off' } : undefined;

    const handleOnChange = event => {
        handleChange({
            event,
            changeClassError,
            formActions,
            onChange,
            profileModel,
            profileModelLabel,
            validations,
        });
    };

    const getCurrentValue = () => {
        if (value && !userProfile[modelPropOrInvalidProp]) {
            return value;
        }

        return userProfile[modelPropOrInvalidProp];
    };

    return (
        <div className={formClassName} style={style} id={formID} ref={currentElement}>
            {label && (
                <label className={classNameLabel} htmlFor={id}>
                    {label}
                </label>
            )}
            {description ? <span>{description}</span> : null}
            {tooltip}
            <Select.Creatable
                id={id}
                className={inputClassName}
                value={getCurrentValue()}
                options={optionsList}
                autoBlur={autoBlur}
                onChange={handleOnChange}
                onFocus={handleOnFocus}
                onBlur={handleOnBlur}
                clearable={clearable}
                onKeyDown={onKeyDown}
                placeholder={placeholder}
                isLoading={isLoading}
                closeTimeOutMS={CLOSE_TIME_OUT_MS}
                searchable={searchable}
                disabled={disabled}
                noResultsText={formatText('validation-noResultsFound')}
                isValidNewOption={isNotValid}
                defaultValue={defaultValue}
                inputProps={inputProps}
                onClose={onClose}
                onOpen={onOpen}
            />
            <Errors model={profileModel} messages={messagesError} component={renderErrors} />
        </div>
    );
};

const mapStateToProps = state => ({
    userProfile: state.userProfile,
    userProfileForm: state.userProfileForm,
});

const mapDispatchToProps = dispatch => ({
    focusActions: bindActionCreators(focusActions, dispatch),
    formActions: bindActionCreators(formActions, dispatch),
});

export const SelectList = connect(mapStateToProps, mapDispatchToProps)(SelectListComponent);
